import getUnixTime from "date-fns/getUnixTime";

import { LogMessage } from "types/generated";

import { ModuleLogsData, StackLogsData } from "./gql";

type RunLogsQueryData = StackLogsData | ModuleLogsData;

// For V8 on 32 bit system https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/length
const MAX_STRING_SIZE = 2 ** 28 - 16;

type ProcessLogsOptions = {
  logsMessages?: Array<Pick<LogMessage, "message">>;
  exists: boolean;
  deleted: boolean;
  expired: boolean;
  loading: boolean;
  error: boolean;
};

export const processLogs = ({
  logsMessages,
  exists,
  deleted,
  expired,
  loading,
  error,
}: ProcessLogsOptions) => {
  // `deleted` implies `expired`, so it has to win to avoid quoting a retention period.
  if (deleted) {
    return "These logs were deleted by an administrator and are no longer available.";
  }

  if (expired) {
    return "These logs have expired. Our standard retention period is 60 days.";
  }

  if (error) {
    return "Could not load logs...";
  }

  const hasLogs = logsMessages && logsMessages.length > 0;

  if (loading) {
    return "Loading logs...";
  } else if (!exists || !hasLogs) {
    // The log stream does not yet exist.
    return "Waiting for logs...";
  }

  let messages = "";

  for (let i = logsMessages.length - 1; i >= 0; i--) {
    const { message } = logsMessages[i];
    if (messages.length + message.length < MAX_STRING_SIZE) {
      messages = `${message}${messages}`;
    } else {
      const diff = MAX_STRING_SIZE - messages.length;
      messages = `${message.slice(diff * -1)}${messages}`;
      break;
    }
  }

  return messages;
};

export const getTimestamp = () => getUnixTime(new Date());

export const getEntityLogs = (data: RunLogsQueryData | undefined) => {
  if (!data) return;

  const entity = "module" in data ? data.module : data.stack;

  return entity?.run?.logs;
};
